/**
 * CloudoX brand mark (v4). Theme-aware light/dark switching via the
 * `.logo-light` / `.logo-dark` CSS in `theme.css` driven by the
 * `data-effective-theme` attribute — no JS / re-render needed, SSR-safe on
 * first paint.
 *
 * Assets must be present at `<public>/brand/logo/v4/` in each app:
 *   logo-v4-bright-bg.png       — wordmark, light background
 *   logo-v4-dark-bg.png         — wordmark, dark background
 *   logo-v4-icon-bright-bg.png  — icon, light background
 *   logo-v4-icon-dark-bg.png    — icon, dark background
 */
export interface LogoProps {
  /** Wordmark (default) or the compact icon. */
  readonly variant?: "wordmark" | "icon";
  /** Rendered pixel height. */
  readonly height?: number;
  readonly className?: string;
  /** Accessible name; set "" to mark decorative when paired with text. */
  readonly title?: string;
}

const BASE = "/brand/logo/v4";

export function Logo({ variant = "wordmark", height = 26, className = "", title = "CloudoX" }: LogoProps) {
  const stem = variant === "icon" ? "logo-v4-icon" : "logo-v4";
  const alt = title;
  return (
    <span className={`inline-flex items-center ${className}`.trim()} style={{ lineHeight: 0 }}>
      <img
        src={`${BASE}/${stem}-bright-bg.png`}
        alt={alt}
        className="logo-light w-auto"
        style={{ height }}
        draggable={false}
      />
      <img
        src={`${BASE}/${stem}-dark-bg.png`}
        alt={alt}
        className="logo-dark w-auto"
        style={{ height }}
        draggable={false}
      />
    </span>
  );
}

export function BrandIcon({ height = 26, className = "", title = "CloudoX" }: Omit<LogoProps, "variant">) {
  return <Logo variant="icon" height={height} className={className} title={title} />;
}
